import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Icon,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDialog } from "muibox";
import {
  useFolhaPendencias,
  useFolhaPendencia,
  useFolhaResumo,
  useReagendarFolha
} from "../../api/ESocialJTServiceApi";
import Page from "../../components/layout/page/Page";
import PageHeader from "../../components/page-header/PageHeader";
import {
  Campo,
  EstadoConsulta,
  Indicadores
} from "../../components/operacional/Elementos";
import { dataExibicao } from "../../shared/apresentacaoEvento";
import useFeedback from "../../components/feedback/useFeedback";

export default function PendenciasPage() {
  const [params, setParams] = useSearchParams();
  const key = params.toString();
  const filtros = useMemo(
    () => Object.fromEntries(new URLSearchParams(key)),
    [key]
  );
  const [referencia, setReferencia] = useState(params.get("referencia") || "");
  const [id, setId] = useState(() => Number(params.get("id")) || null);
  const page = Math.max(0, Number(filtros.page) || 0);
  const query = useFolhaPendencias({ ...filtros, page, size: 25 });
  const resumo = useFolhaResumo();
  const detalhe = useFolhaPendencia(id);
  const reagendar = useReagendarFolha();
  const dialog = useDialog();
  const feedback = useFeedback();
  const r = resumo.data;
  const p = detalhe.data;
  function filtrar(k, v) {
    const next = new URLSearchParams(params);
    next.set(k, v);
    next.set("page", "0");
    setParams(next);
  }
  async function tentar() {
    try {
      await dialog.confirm({
        title: `Reagendar a pendência #${p.id}?`,
        message:
          "Confirme que a causa foi corrigida. A entrega mantém seu conteúdo original e será processada na ordem da fila.",
        ok: { text: "Reagendar", color: "primary" },
        cancel: { text: "Cancelar" }
      });
    } catch {
      return;
    }
    reagendar.mutate(p.id, {
      onSuccess: () =>
        feedback.showSuccess(
          "Entrega reagendada. Acompanhe a próxima tentativa."
        ),
      onError: feedback.showError
    });
  }
  return (
    <Page maxWidth="lg">
      <PageHeader
        primary="Sincronização com a Folha"
        secondary="Consulte entregas pendentes, identifique bloqueios e acompanhe novas tentativas."
        commands={[
          <Button
            key="atualizar"
            startIcon={<Icon>refresh</Icon>}
            onClick={() => {
              query.refetch();
              resumo.refetch();
            }}
          >
            Atualizar
          </Button>
        ]}
      />
      <EstadoConsulta query={resumo}>
        {r && (
          <>
            <Indicadores
              items={[
                {
                  label: "Pendentes",
                  value: r.contagens.PENDENTE,
                  icon: "schedule"
                },
                {
                  label: "Bloqueadas",
                  value: r.contagens.BLOQUEADA,
                  icon: "pause_circle",
                  color: "error.main"
                },
                {
                  label: "Aplicadas",
                  value: r.contagens.APLICADA,
                  icon: "done_all",
                  color: "success.main"
                }
              ]}
            />
            {!r.habilitada && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                A sincronização persistente está desabilitada. O reagendamento
                não está disponível.
              </Alert>
            )}
            {r.primeiraPendencia && (
              <Alert
                severity={
                  r.primeiraPendencia.situacao === "BLOQUEADA"
                    ? "error"
                    : "info"
                }
                sx={{ mb: 3 }}
                action={
                  <Button
                    color="inherit"
                    onClick={() => setId(r.primeiraPendencia.id)}
                  >
                    Consultar
                  </Button>
                }
              >
                A fila aguarda a entrega #{r.primeiraPendencia.id}.{" "}
                {r.primeiraPendencia.situacao === "BLOQUEADA"
                  ? "Corrija a causa e reagende essa entrega para liberar a sequência."
                  : "As entregas seguem a ordem de criação, respeitando o intervalo entre tentativas."}
              </Alert>
            )}
          </>
        )}
      </EstadoConsulta>
      <Paper
        variant="outlined"
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          filtrar("referencia", referencia.trim());
        }}
        sx={{ p: 2, mb: 2, borderRadius: 2 }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Situação"
              value={filtros.situacao || ""}
              onChange={(e) => filtrar("situacao", e.target.value)}
            >
              {[
                ["", "Pendentes e bloqueadas"],
                ["PENDENTE", "Pendente"],
                ["BLOQUEADA", "Bloqueada"],
                ["APLICADA", "Aplicada"]
              ].map(([v, l]) => (
                <MenuItem key={v} value={v}>
                  {l}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Tipo da entrega"
              value={filtros.tipo || ""}
              onChange={(e) => filtrar("tipo", e.target.value)}
            >
              {[
                ["", "Todos"],
                ["OCORRENCIA", "Ocorrência"],
                ["TOTALIZADOR", "Totalizador"],
                ["FECHAMENTO", "Fechamento"]
              ].map(([v, l]) => (
                <MenuItem key={v} value={v}>
                  {l}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Referência exata"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              helperText="ID do evento ou recibo de origem do totalizador"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button type="submit" variant="contained" fullWidth>
              Consultar
            </Button>
          </Grid>
        </Grid>
      </Paper>
      <EstadoConsulta query={query}>
        {!query.data?.content?.length ? (
          <Alert severity="info">
            Nenhuma entrega encontrada neste filtro.
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label="Pendências da Folha">
              <TableHead>
                <TableRow>
                  <TableCell>Entrega</TableCell>
                  <TableCell>Referência</TableCell>
                  <TableCell>Situação</TableCell>
                  <TableCell>Tentativas</TableCell>
                  <TableCell>Próxima tentativa</TableCell>
                  <TableCell>Erro</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.content.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      #{p.id}
                      <Typography variant="caption" display="block">
                        {p.tipo}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ overflowWrap: "anywhere" }}>
                      {p.referencia}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={p.situacao}
                        color={
                          p.situacao === "BLOQUEADA"
                            ? "error"
                            : p.situacao === "APLICADA"
                            ? "success"
                            : "default"
                        }
                      />
                    </TableCell>
                    <TableCell>{p.tentativas}</TableCell>
                    <TableCell>
                      {p.situacao === "APLICADA"
                        ? "Concluída"
                        : dataExibicao(p.proximaTentativa)}
                    </TableCell>
                    <TableCell>{p.codigoErro || "—"}</TableCell>
                    <TableCell>
                      <Button onClick={() => setId(p.id)}>Detalhes</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </EstadoConsulta>
      {query.data?.totalPages > 0 && (
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
          <Typography variant="body2">
            {query.data.totalElements} entregas
          </Typography>
          <Pagination
            count={query.data.totalPages}
            page={page + 1}
            onChange={(_, v) => {
              const next = new URLSearchParams(params);
              next.set("page", String(v - 1));
              setParams(next);
            }}
          />
        </Stack>
      )}
      <Dialog
        open={Boolean(id)}
        onClose={() => setId(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Entrega #{id}</DialogTitle>
        <DialogContent>
          <EstadoConsulta query={detalhe}>
            {p && (
              <Stack spacing={2}>
                <Chip
                  label={p.situacao}
                  color={
                    p.situacao === "BLOQUEADA"
                      ? "error"
                      : p.situacao === "APLICADA"
                      ? "success"
                      : "default"
                  }
                />
                <Campo
                  label="Tipo / referência"
                  value={`${p.tipo} · ${p.referencia}`}
                />
                <Campo label="Criada em" value={dataExibicao(p.criadaEm)} />
                <Campo label="Tentativas" value={p.tentativas} />
                <Campo
                  label="Próxima tentativa"
                  value={dataExibicao(p.proximaTentativa)}
                />
                <Campo label="Aplicada em" value={dataExibicao(p.aplicadaEm)} />
                <Campo label="Código do erro" value={p.codigoErro} />
                <Campo label="Resultado" value={p.resultado} />
                <Campo label="Chave de rastreamento" value={p.chave} />
                {p.situacao === "PENDENTE" && p.tentativas === 0 && (
                  <Alert severity="info">
                    Esta entrega aguarda processamento automático. Não é
                    necessário reagendá-la.
                  </Alert>
                )}
              </Stack>
            )}
          </EstadoConsulta>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setId(null)}>Fechar</Button>
          <Button
            variant="contained"
            disabled={
              !p ||
              p.situacao === "APLICADA" ||
              (!p.tentativas && p.situacao === "PENDENTE") ||
              !r?.habilitada ||
              reagendar.isLoading
            }
            onClick={tentar}
          >
            Reagendar entrega
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
